import { Box, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import type React from 'react'

import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'
import { LearnerFeedbackAttributeTag } from '@/client/services/learnerFeedback/constants'
import getPositionInSeries from '@/client/services/session/getPositionInSeries'

import AttributeTag from '@/components/LearnerFeedback/AttributeTag'
import MessageBox from '@/components/UI/MessageBox'
import { userDetailsFromQuery } from '@/components/Worlds/transforms/detailsFromQueries'
import AvatarWithName, {
  USER_FIELDS_FOR_AVATAR,
} from '@/components/Worlds/UI/Avatar/AvatarWithName'
import Card from '@/components/Worlds/UI/Card'

export default function LearnerFeedbackDisplay({
  feedback,
  showRecipient = false,
  showType = false,
}: {
  feedback: Query
  showRecipient?: boolean
  showType?: boolean
}): React.ReactElement {
  const reviewer = feedback.one(UserQ.where({ id: LearnerFeedbackQ.reviewer_user_id }))
  const reviewee = feedback.one(UserQ.where({ id: LearnerFeedbackQ.reviewee_user_id }))

  const reviewerUser = userDetailsFromQuery(USER_FIELDS_FOR_AVATAR, { user: reviewer })
  const revieweeUser = userDetailsFromQuery(USER_FIELDS_FOR_AVATAR, { user: reviewee })

  const isSelfReflection = revieweeUser.id === reviewerUser.id

  const createdAt = feedback(LearnerFeedbackQ.created_at) ?? new Date().toISOString()

  const comment = feedback(LearnerFeedbackQ.comment)
  const tags = feedback(LearnerFeedbackQ.attribute_tags) ?? []

  return (
    <Card px="32px" py="28px">
      <Box display="flex" alignItems="center" gap="18px" mb="12px">
        <Box display="flex" alignItems="center" gap="10px">
          <AvatarWithName
            user={showRecipient ? revieweeUser : reviewerUser}
            size="md"
            distinguishSelf
          />
          {showType ? (
            <Tag variant="outline" px="8px" py="0px" ml="4px">
              {`${isSelfReflection ? 'Self' : 'Peer'} Reflection`}
            </Tag>
          ) : (
            <Tag variant="outline" px="8px" py="0px" ml="4px">
              Session {getPositionInSeries(feedback)}
            </Tag>
          )}
          <Text color="fg.subtle" fontSize="sm">
            {DateTime.fromISO(createdAt).toFormat('MMM d, yyyy')}
          </Text>
        </Box>
      </Box>
      {comment != null && comment.length > 0 ? (
        <MessageBox
          text={comment}
          hidePointer={showRecipient}
          {...(isSelfReflection ? { bgColor: 'bg.success' } : {})}
        />
      ) : null}
      <Wrap spacing="16px" mt="16px" align="center" justify={{ base: 'center', md: 'flex-start' }}>
        {tags.map(tag => {
          const tagValue = toEnumValueOrNull(LearnerFeedbackAttributeTag, tag)

          return tagValue == null ? null : (
            <WrapItem key={tagValue}>
              <AttributeTag tagValue={tagValue} />
            </WrapItem>
          )
        })}
      </Wrap>
    </Card>
  )
}
